/**
 * Planilla general de inscripción en PDF — la versión para imprimir, firmar y
 * entregar. Carta horizontal: con once columnas, en vertical los nombres se
 * parten y la planilla deja de ser legible.
 *
 * Se dibuja a mano (sin jspdf-autotable, que no está en el proyecto). Dos
 * cosas gobiernan el diseño:
 *
 *  · COMPACTA. Un club de 30 niños tiene que caber en una hoja, no en tres.
 *    De ahí la fila de 4,8mm, el margen de 9mm y que el tipo y el número de
 *    documento vayan en una sola columna ("TI 1.095.274"): son un dato, no
 *    dos, y separados se comían el ancho que necesitan los nombres.
 *  · SIN REJILLA. Rayas horizontales finísimas en vez de recuadrar cada
 *    celda. Una tabla con todos los bordes se lee peor y, en papel, la tinta
 *    de la rejilla compite con los datos.
 *
 * Los anchos van como PROPORCIONES del ancho útil, nunca en milímetros
 * fijos: con fijos la fila sumaba 271mm contra los 259 de la carta
 * horizontal y la última columna se salía del papel sin que nada avisara.
 */
import { jsPDF } from "jspdf";
import {
  planillaFilename,
  type PlanillaData,
  type PlanillaRow,
  type PlanillaSection,
} from "@/lib/planilla/planillaInscripcion";

// ── Medidas (mm) ──
const MARGIN = 9;
const ROW_H = 4.8;
const HEAD_H = 6.4;
const BAND_H = 15;

// ── Paleta: el morado de Insignia y una escala de grises fría ──
type RGB = [number, number, number];
const MORADO: RGB = [91, 33, 182]; // violet-800, la banda del encabezado
const MORADO_CLARO: RGB = [237, 233, 254]; // violet-100, bloques de niños
const ACENTO_NINOS: RGB = [124, 58, 237]; // violet-600
const ACENTO_EQUIPO: RGB = [71, 85, 105]; // slate-600
const GRIS_BLOQUE: RGB = [241, 245, 249]; // slate-100
const RAYA: RGB = [226, 232, 240]; // slate-200
const CEBRA: RGB = [250, 250, 252];
const TEXTO: RGB = [15, 23, 42]; // slate-900
const APAGADO: RGB = [100, 116, 139]; // slate-500

const fill = (pdf: jsPDF, c: RGB) => pdf.setFillColor(c[0], c[1], c[2]);
const stroke = (pdf: jsPDF, c: RGB) => pdf.setDrawColor(c[0], c[1], c[2]);
const ink = (pdf: jsPDF, c: RGB) => pdf.setTextColor(c[0], c[1], c[2]);

type ColDef = {
  title: string;
  /** Peso relativo; se reparte sobre el ancho útil. */
  w: number;
  align?: "left" | "center" | "right";
  value: (r: PlanillaRow) => string;
};

/** Las secciones de niños se tiñen de morado; las del equipo, de gris. */
const esDeNinos = (s: PlanillaSection) => s.showBaptized;

/**
 * La geometría se calcula SIEMPRE con las once columnas, también en los
 * bloques de adultos, y ahí el hueco de "Bautizado" se lo queda "Cargo".
 * Así las diez primeras columnas caen en la misma x en todos los bloques:
 * calculando cada bloque por su cuenta, las tablas de niños y las de adultos
 * quedaban desalineadas entre sí y la hoja parecía mal armada.
 */
function columns(showBaptized: boolean, usableW: number): ColDef[] {
  const base: ColDef[] = [
    { title: "N°", w: 7, align: "center", value: (r) => String(r.order) },
    { title: "Nombres y apellidos", w: 62, value: (r) => r.fullName },
    {
      title: "Documento",
      w: 30,
      // Tipo y número juntos: separados gastaban una columna entera en
      // repetir "TI" treinta veces.
      value: (r) =>
        [r.documentType, r.documentNumber].filter(Boolean).join(" "),
    },
    { title: "Nacimiento", w: 21, align: "center", value: (r) => r.birthDate },
    {
      title: "Edad",
      w: 10,
      align: "center",
      value: (r) => (r.age === "" ? "" : String(r.age)),
    },
    {
      title: "Sexo",
      w: 9,
      align: "center",
      value: (r) => r.gender.slice(0, 1).toUpperCase(),
    },
    { title: "RH", w: 9, align: "center", value: (r) => r.bloodType },
    { title: "EPS / Seguro", w: 32, value: (r) => r.healthInsurance },
    { title: "Teléfono", w: 23, value: (r) => r.phone },
    { title: "Cargo / Entrada", w: 42, value: (r) => r.role },
  ];
  const BAUTIZ_W = 13;
  const sum = base.reduce((t, c) => t + c.w, 0) + BAUTIZ_W;
  const k = usableW / sum;
  const cols = base.map((c) => ({ ...c, w: c.w * k }));
  if (showBaptized) {
    cols.push({
      title: "Bautiz.",
      w: BAUTIZ_W * k,
      align: "center" as const,
      value: (r: PlanillaRow) => r.baptized,
    });
  } else {
    // El ancho sobrante va a "Cargo / Entrada", la columna que más lo
    // agradece: ahí caben "Ecónomo(a) Auxiliar" y "Anciano Consejero".
    cols[cols.length - 1].w += BAUTIZ_W * k;
  }
  return cols;
}

/** Recorta al ancho disponible en vez de desbordar o partir la fila. */
function fit(pdf: jsPDF, text: string, width: number): string {
  if (!text) return "";
  const lines = pdf.splitTextToSize(text, width - 1.8) as string[];
  return lines[0] ?? "";
}

function cell(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  w: number,
  h: number,
  align: "left" | "center" | "right" = "left",
) {
  const baseline = y + h / 2 + 1.05;
  const t = fit(pdf, text, w);
  if (align === "center") pdf.text(t, x + w / 2, baseline, { align: "center" });
  else if (align === "right") pdf.text(t, x + w - 1.2, baseline, { align: "right" });
  else pdf.text(t, x + 1.2, baseline);
}

export function downloadPlanillaPdf(data: PlanillaData): void {
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter",
  });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usableW = pageW - MARGIN * 2;
  const bottom = pageH - MARGIN - 6; // deja sitio al pie
  const { header, sections, totals } = data;

  let y = MARGIN;
  const newPage = () => {
    pdf.addPage();
    y = MARGIN;
  };
  const ensure = (needed: number) => {
    if (y + needed > bottom) newPage();
  };

  // ════════ BANDA DEL ENCABEZADO ════════
  fill(pdf, MORADO);
  pdf.rect(MARGIN, y, usableW, BAND_H, "F");
  ink(pdf, [255, 255, 255]);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12.5);
  pdf.text("PLANILLA GENERAL DE INSCRIPCIÓN", MARGIN + 4, y + 6.4);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(216, 203, 255);
  const sub = [header.eventName, header.eventDates].filter(Boolean).join("  ·  ");
  pdf.text(fit(pdf, sub, usableW * 0.55), MARGIN + 4, y + 11.4);
  // El club, a la derecha y grande: es lo primero que busca quien recibe.
  ink(pdf, [255, 255, 255]);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.text(fit(pdf, header.clubName, usableW * 0.4), MARGIN + usableW - 4, y + 7.2, {
    align: "right",
  });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(200, 184, 255);
  pdf.text(`${totals.total} inscritos`, MARGIN + usableW - 4, y + 11.6, {
    align: "right",
  });
  y += BAND_H + 3;

  // ════════ DATOS DEL CLUB ════════
  // Panel sin rejilla: etiquetas chiquitas arriba, valores debajo. Recuadrar
  // ocho celdas para ocho palabras era gastar tinta y altura.
  const infoH = 13.5;
  fill(pdf, GRIS_BLOQUE);
  pdf.roundedRect(MARGIN, y, usableW, infoH, 1.2, 1.2, "F");
  // Anchos ponderados, no iguales: en siete columnas idénticas "Iglesia
  // Central de Bucaramanga" se cortaba en "Iglesia Central de" mientras
  // "Zona" sobraba espacio.
  const campos: [string, string, number][] = [
    ["IGLESIA", header.churchName, 1.6],
    ["DISTRITO", header.districtName, 1.1],
    ["ZONA", header.zone, 1],
    ["PASTOR ASISTENTE", header.assistantPastor, 1.3],
    ["MINISTRO", header.minister, 1.3],
    ["DIRECTOR(A)", header.director, 1.6],
    ["TELÉFONO", header.contactPhone, 0.9],
  ];
  const pesoTotal = campos.reduce((t, c) => t + c[2], 0);
  let cx = MARGIN;
  campos.forEach(([etiqueta, valor, peso]) => {
    const w = (usableW * peso) / pesoTotal;
    ink(pdf, APAGADO);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(5.6);
    pdf.text(fit(pdf, etiqueta, w - 4), cx + 3, y + 5);
    ink(pdf, TEXTO);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text(fit(pdf, valor || "—", w - 4), cx + 3, y + 10);
    cx += w;
  });
  y += infoH + 4;

  // ════════ BLOQUES DE PERSONAS ════════
  const sectionHead = (section: PlanillaSection, cont = false) => {
    const acento = esDeNinos(section) ? ACENTO_NINOS : ACENTO_EQUIPO;
    fill(pdf, esDeNinos(section) ? MORADO_CLARO : GRIS_BLOQUE);
    pdf.roundedRect(MARGIN, y, usableW, HEAD_H, 1, 1, "F");
    // Barrita de color a la izquierda: agrupa de un vistazo niños vs equipo.
    fill(pdf, acento);
    pdf.rect(MARGIN, y + 0.6, 1.6, HEAD_H - 1.2, "F");
    ink(pdf, acento);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text(
      `${section.code}.  ${section.title}${cont ? "  (continúa)" : ""}`,
      MARGIN + 4,
      y + HEAD_H / 2 + 1.1,
    );
    pdf.setFontSize(8);
    pdf.text(String(section.rows.length), MARGIN + usableW - 3, y + HEAD_H / 2 + 1.1, {
      align: "right",
    });
    y += HEAD_H + 0.8;
  };

  const columnHead = (cols: ColDef[], section: PlanillaSection) => {
    ink(pdf, APAGADO);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.2);
    let x = MARGIN;
    for (const c of cols) {
      cell(pdf, c.title.toUpperCase(), x, y, c.w, ROW_H, c.align);
      x += c.w;
    }
    y += ROW_H;
    stroke(pdf, esDeNinos(section) ? ACENTO_NINOS : ACENTO_EQUIPO);
    pdf.setLineWidth(0.35);
    pdf.line(MARGIN, y, MARGIN + usableW, y);
    pdf.setLineWidth(0.1);
  };

  for (const section of sections) {
    const cols = columns(section.showBaptized, usableW);
    // Un bloque nunca empieza al filo: encabezado y tres filas van juntos.
    ensure(HEAD_H + ROW_H * 4);
    sectionHead(section);
    columnHead(cols, section);

    section.rows.forEach((row, idx) => {
      if (y + ROW_H > bottom) {
        newPage();
        sectionHead(section, true);
        columnHead(cols, section);
      }
      if (idx % 2 === 1) {
        fill(pdf, CEBRA);
        pdf.rect(MARGIN, y, usableW, ROW_H, "F");
      }
      ink(pdf, TEXTO);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6.9);
      let x = MARGIN;
      for (const c of cols) {
        cell(pdf, c.value(row), x, y, c.w, ROW_H, c.align);
        x += c.w;
      }
      y += ROW_H;
      stroke(pdf, RAYA);
      pdf.line(MARGIN, y, MARGIN + usableW, y);
    });
    y += 3.5;
  }

  // ════════ TOTALES ════════
  const cardH = 15;
  ensure(cardH + 22);
  const tarjetas: [string, string, RGB][] = [
    ["AVENTUREROS", String(totals.participants), ACENTO_NINOS],
    ["DIRECTIVA Y APOYO", String(totals.adults), ACENTO_EQUIPO],
    ["TOTAL DE INSCRITOS", String(totals.total), MORADO],
    [
      "VALOR DE LAS INSCRIPCIONES",
      totals.amount.toLocaleString("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0,
      }),
      ACENTO_EQUIPO,
    ],
  ];
  const cardW = (usableW - 3 * 3) / 4;
  tarjetas.forEach(([etiqueta, valor, color], i) => {
    const x = MARGIN + i * (cardW + 3);
    fill(pdf, i === 2 ? MORADO_CLARO : GRIS_BLOQUE);
    pdf.roundedRect(x, y, cardW, cardH, 1.2, 1.2, "F");
    fill(pdf, color);
    pdf.rect(x, y + 0.8, 1.6, cardH - 1.6, "F");
    ink(pdf, APAGADO);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(5.6);
    pdf.text(etiqueta, x + 4, y + 5);
    ink(pdf, color);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(valor.length > 8 ? 10 : 13);
    pdf.text(valor, x + 4, y + 12);
  });
  y += cardH + 16;

  // ════════ FIRMAS ════════
  ensure(12);
  stroke(pdf, APAGADO);
  const firmaW = (usableW - 24) / 2;
  pdf.line(MARGIN, y, MARGIN + firmaW, y);
  pdf.line(MARGIN + firmaW + 24, y, MARGIN + usableW, y);
  ink(pdf, APAGADO);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.text("Firma del director(a)", MARGIN, y + 3.6);
  pdf.text("Firma del pastor", MARGIN + firmaW + 24, y + 3.6);

  // ════════ PIE EN TODAS LAS PÁGINAS ════════
  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    pdf.setPage(p);
    stroke(pdf, RAYA);
    pdf.line(MARGIN, pageH - MARGIN - 3.5, pageW - MARGIN, pageH - MARGIN - 3.5);
    ink(pdf, APAGADO);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.2);
    pdf.text(
      `${header.clubName}  ·  Generada en Insignia el ${header.generatedAt}`,
      MARGIN,
      pageH - MARGIN,
    );
    pdf.text(`${p} / ${pages}`, pageW - MARGIN, pageH - MARGIN, {
      align: "right",
    });
  }

  pdf.save(`${planillaFilename(header.clubName, header.eventName)}.pdf`);
}
